/**
 * Oddit size guide drawer — drill-down navigation.
 *
 * The drawer body holds a root "list" view plus one full-panel detail page per
 * accordion block. Tapping a list row opens its page (slides in from the right);
 * a back row returns to the list. Closing the modal resets back to the list.
 *
 * Vanilla, no-build, delegated so it survives theme-editor re-renders. Native
 * popover Escape / backdrop dismiss is left untouched (closes the whole modal).
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.oddit-size-guide-drawer__view--root';

  function getViews(el) {
    return el.closest('[data-size-guide-views]');
  }

  function getScroller(views) {
    return views.closest('.oddit-size-guide-drawer');
  }

  function resetScroll(views) {
    var scroller = getScroller(views);
    if (scroller) scroller.scrollTop = 0;
  }

  function showRoot(views, animateReturn) {
    var root = views.querySelector(ROOT_SELECTOR);
    views
      .querySelectorAll('.oddit-size-guide-drawer__view--detail')
      .forEach(function (page) {
        page.hidden = true;
      });
    if (!root) return;
    root.hidden = false;
    if (animateReturn) {
      root.classList.add('is-returning');
      root.addEventListener(
        'animationend',
        function handle() {
          root.classList.remove('is-returning');
          root.removeEventListener('animationend', handle);
        },
        { once: true },
      );
    }
  }

  function openDetail(trigger) {
    var views = getViews(trigger);
    if (!views) return;
    var targetId = trigger.getAttribute('data-size-guide-open');
    var page = targetId && document.getElementById(targetId);
    if (!page) return;

    var root = views.querySelector(ROOT_SELECTOR);
    if (root) root.hidden = true;
    page.hidden = false;

    // Remember the row so focus returns to it when the page is dismissed.
    views._osgdReturnFocus = trigger;

    resetScroll(views);
    var back = page.querySelector('[data-size-guide-back]');
    if (back) back.focus();
  }

  function closeDetail(back) {
    var views = getViews(back);
    if (!views) return;

    showRoot(views, true);
    resetScroll(views);

    var trigger = views._osgdReturnFocus;
    if (trigger && typeof trigger.focus === 'function') trigger.focus();
    views._osgdReturnFocus = null;
  }

  // Reset a hosted video back to its thumbnail state: poster shown, controls
  // hidden, play overlay restored.
  function resetVideo(container, video) {
    container.classList.remove('is-playing');
    if (!video) return;
    video.controls = false;
    if (typeof video.pause === 'function') video.pause();
    // load() restores the poster frame (currentTime = 0 would show frame 0).
    if (typeof video.load === 'function') video.load();
  }

  function playVideo(trigger) {
    var container = trigger.closest('[data-size-guide-video]');
    if (!container) return;
    var video = container.querySelector('.oddit-size-guide-drawer__video-player');
    if (!video) return;
    container.classList.add('is-playing');
    video.controls = true;
    var played = video.play();
    if (played && typeof played.catch === 'function') played.catch(function () {});
  }

  // Restore the thumbnail + play button once a video finishes (guard rebinding).
  function bindVideos(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('.oddit-size-guide-drawer__video-player')
      .forEach(function (video) {
        if (video.dataset.osgdVideoBound === 'true') return;
        video.dataset.osgdVideoBound = 'true';
        video.addEventListener('ended', function () {
          var container = video.closest('[data-size-guide-video]');
          if (container) resetVideo(container, video);
        });
      });
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-size-guide-open]');
    if (opener) {
      openDetail(opener);
      return;
    }
    var back = e.target.closest && e.target.closest('[data-size-guide-back]');
    if (back) {
      closeDetail(back);
      return;
    }
    var play = e.target.closest && e.target.closest('[data-size-guide-video-play]');
    if (play) {
      playVideo(play);
      return;
    }
    var unitBtn = e.target.closest && e.target.closest('[data-osgd-unit-btn]');
    if (unitBtn) {
      setUnit(unitBtn.closest('.oddit-size-guide-drawer'), unitBtn.getAttribute('data-osgd-unit-btn'), true);
    }
  });

  // Measurement units: the server picks a default by country (in for the US,
  // cm elsewhere); a shopper's own choice is remembered for later visits.
  var UNIT_KEY = 'osgdUnit';

  function setUnit(drawer, unit, remember) {
    if (!drawer || (unit !== 'in' && unit !== 'cm')) return;
    drawer.setAttribute('data-osgd-unit', unit);
    drawer.querySelectorAll('[data-osgd-unit-btn]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-osgd-unit-btn') === unit ? 'true' : 'false');
    });
    if (remember) {
      try {
        localStorage.setItem(UNIT_KEY, unit);
      } catch (err) {}
    }
  }

  function applySavedUnit(root) {
    var saved = null;
    try {
      saved = localStorage.getItem(UNIT_KEY);
    } catch (err) {}
    if (!saved) return;
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.oddit-size-guide-drawer[data-osgd-unit]').forEach(function (drawer) {
      setUnit(drawer, saved, false);
    });
  }

  // Reset to the list whenever the modal closes, so it always reopens on the
  // root view. The toggle event doesn't bubble, so bind per modal (guarded).
  function bindModalReset(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll('.oddit-size-guide-drawer__modal[popover]')
      .forEach(function (modal) {
        if (modal.dataset.osgdResetBound === 'true') return;
        modal.dataset.osgdResetBound = 'true';
        modal.addEventListener('toggle', function (e) {
          if (e.newState === 'open') return;
          modal
            .querySelectorAll('[data-size-guide-views]')
            .forEach(function (views) {
              showRoot(views, false);
              views._osgdReturnFocus = null;
            });
          // Reset any video back to its thumbnail state.
          modal
            .querySelectorAll('[data-size-guide-video]')
            .forEach(function (container) {
              resetVideo(
                container,
                container.querySelector('.oddit-size-guide-drawer__video-player'),
              );
            });
        });
      });
  }

  function init() {
    bindModalReset(document);
    bindVideos(document);
    applySavedUnit(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders the section without a full page load.
  document.addEventListener('shopify:section:load', function (e) {
    bindModalReset(e.target || document);
    bindVideos(e.target || document);
    applySavedUnit(e.target || document);
  });
})();
